import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { FirstReleaseChecklist } from '../../onboarding/checklist/FirstReleaseChecklist';
import { HomeAiNudge } from './aiNudge/HomeAiNudge';
import { SongsBrewHomeCard } from './songsBrew/SongsBrewHomeCard';
import { WatermarkHomeCard } from '../watermarkUpsell/trial/WatermarkHomeCard';
import { AudienceRow } from './AudienceRow';
import { StreamsRow } from './StreamsRow';
import { CatalogBlock } from './CatalogBlock';
import { SmartlinksBlock } from './SmartlinksBlock';
import { PromoteBlock } from './PromoteBlock';
import { SocialBlock } from './SocialBlock/SocialBlock';
import { useHomeSocialSummary } from './SocialBlock/useHomeSocialSummary';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { HomePeriodDays } from './homePeriod';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ArtistHomeContentProps {
  accountId: number;
  days: HomePeriodDays;
}

/**
 * The body of the artist Home, top to bottom (design a1): the watermark card of the trial upsell
 * (design: trial-upsell b2), the "Your first release" checklist
 * (design: onboarding d1-d4), the AI assistants card (design: ai-assistants n1), the SongsBrew
 * newsletter card (design: songsbrew-promotion a1), the two KPI rows,
 * Catalog + Smartlinks performance, then Social (design
 * music24-social-connectors d1) and Promote. Each part reads its own endpoints and loads and
 * fails on its own (owner decision 4).
 */
const ArtistHomeContentComponent: React.FC<ArtistHomeContentProps> = ({ accountId, days }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openCatalog = useCallback(() => router.push(SMLRoutes.catalog), [router]);
  const openInsights = useCallback(() => router.push(SMLRoutes.streaming), [router]);
  const openSmartlinks = useCallback(() => router.push(SMLRoutes.smartlinks), [router]);
  // The read of the Social block lives here, because the Promote block needs one flag of it.
  const social = useHomeSocialSummary(accountId, days);

  return (
    <div className={styles.page}>
      {/* design: trial-upsell b2. The first card, from day 3 of the trial and on Free. */}
      <WatermarkHomeCard />
      <FirstReleaseChecklist />
      <HomeAiNudge />
      {/* design: songsbrew-promotion a1. The SongsBrew newsletter card, after the AI assistants card. */}
      <SongsBrewHomeCard />
      <div className={styles.rows} data-tour="home">
        <div className={styles.sectionKey}>{intl.formatMessage(messages.sectionAudience)}</div>
        <AudienceRow accountId={accountId} days={days} onOpenCatalog={openCatalog} />
        <div className={`${styles.sectionKey} mt-1`}>{intl.formatMessage(messages.sectionStreams)}</div>
        <StreamsRow
          accountId={accountId}
          days={days}
          onOpenInsights={openInsights}
          onOpenCatalog={openCatalog}
          onOpenSmartlinks={openSmartlinks}
        />
      </div>
      <div className={styles.blocksGrid}>
        <CatalogBlock accountId={accountId} onOpenCatalog={openCatalog} />
        <SmartlinksBlock accountId={accountId} days={days} onOpenSmartlinks={openSmartlinks} />
      </div>
      <SocialBlock summary={social} days={days} />
      <PromoteBlock accountId={accountId} isConnectCardHidden={social.ownsConnectCard} />
    </div>
  );
};

export const ArtistHomeContent = TypedMemo(ArtistHomeContentComponent);
